import { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Stack, Typography, Chip,
  TextField, MenuItem, Button, CircularProgress, Alert,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper,
  InputAdornment, Tooltip, Switch, FormControlLabel,
  ToggleButtonGroup, ToggleButton, Autocomplete,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ReceiptLong as ReceiptIcon,
  Sms as SmsIcon, WhatsApp as WhatsAppIcon,
  PhoneAndroid as MomoIcon, AccountBalance as BankIcon,
  EventNote as DateIcon,
  Groups as CollectionIcon,
  ListAlt as SheetIcon, Print as PrintIcon,
  Add as AddIcon, ShoppingCart as CartIcon,
} from '@mui/icons-material';
import API from '../../BaseURL';
import swal from '../../utils/swal';
import OtherReceiptDialog from './OtherReceiptDialog';
import CollectionSheetDialog from './CollectionSheetDialog';

const FONT_ACCT = `'IBM Plex Sans', 'Inter', sans-serif`;
const TNUM = { fontVariantNumeric: 'tabular-nums' };
const fmtMoney = (n) =>
  Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const PAYMENT_MODES = [
  { v: 'Cash',          label: 'Cash' },
  { v: 'Bank Transfer', label: 'Bank Transfer' },
  { v: 'Mobile Money',  label: 'Mobile Money' },
  { v: 'Cheque',        label: 'Cheque' },
  { v: 'POS',           label: 'POS / Card' },
];
const TERMS = ['Term_1', 'Term_2', 'Term_3'];

function defaultAcademicYear() {
  const d = new Date();
  const y = d.getFullYear();
  return d.getMonth() >= 7 ? `${y}/${y + 1}` : `${y - 1}/${y}`;
}

// ── Common payment mode + bank panel reused across the three forms ───
function PaymentModePanel({ values, set, sendSMS, setSendSMS, sendWA, setSendWA, bankAccts }) {
  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1}>
        <TextField size="small" type="date" label="Date" InputLabelProps={{ shrink: true }}
          value={values.pay_date} onChange={(e) => set('pay_date', e.target.value)}
          sx={{ flex: 1 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><DateIcon fontSize="small" /></InputAdornment> }} />
        <TextField select size="small" label="Mode" value={values.mode}
          onChange={(e) => set('mode', e.target.value)} sx={{ flex: 1 }}>
          {PAYMENT_MODES.map(m => <MenuItem key={m.v} value={m.v}>{m.label}</MenuItem>)}
        </TextField>
      </Stack>
      <TextField select size="small" label="Bank / Cash Account" value={values.bank_account}
        onChange={(e) => set('bank_account', e.target.value)} fullWidth
        InputProps={{ startAdornment: <InputAdornment position="start"><BankIcon fontSize="small" /></InputAdornment> }}>
        <MenuItem value=""><em>— Select —</em></MenuItem>
        {bankAccts.map(b => (
          <MenuItem key={b.trans_code} value={b.name}>
            {b.name} <Box component="span" sx={{ ml: 0.5, color: 'text.disabled', fontSize: 11 }}>· {b.account_type || ''}</Box>
          </MenuItem>
        ))}
      </TextField>
      <Stack direction="row" spacing={1}>
        <TextField size="small" fullWidth label="Reference / Receipt No"
          value={values.reference_no} onChange={(e) => set('reference_no', e.target.value)} />
        <TextField size="small" fullWidth label="Notes"
          value={values.notes} onChange={(e) => set('notes', e.target.value)} />
      </Stack>

      {/* Compact "Send / Trigger" — single icon row instead of stacked block */}
      <Stack direction="row" alignItems="center" spacing={0.5}
        sx={{ p: 0.6, borderRadius: 1, border: (t) => `1px dashed ${alpha(t.palette.primary.main, 0.3)}` }}>
        <Typography variant="caption" sx={{
          textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 800, fontSize: 9,
          color: 'text.secondary', px: 0.5,
        }}>
          Send · soon
        </Typography>
        <Box sx={{ flex: 1 }} />
        <FormControlLabel sx={{ m: 0 }}
          control={<Switch size="small" disabled checked={sendSMS}
            onChange={(e) => setSendSMS(e.target.checked)} />}
          label={<SmsIcon fontSize="small" sx={{ color: 'text.secondary' }} />} />
        <FormControlLabel sx={{ m: 0 }}
          control={<Switch size="small" disabled checked={sendWA}
            onChange={(e) => setSendWA(e.target.checked)} />}
          label={<WhatsAppIcon fontSize="small" sx={{ color: 'text.secondary' }} />} />
        <Tooltip title="MoMo trigger coming soon">
          <span><Button size="small" variant="outlined" disabled startIcon={<MomoIcon fontSize="small" />}
            sx={{ textTransform: 'none', fontWeight: 700, ml: 0.5, px: 1, minWidth: 0 }}>
            MoMo
          </Button></span>
        </Tooltip>
      </Stack>
    </Stack>
  );
}

// ── Itemised lines — shared by both forms ─────────────────────
// One payment can cover several things at once: a uniform at 120 and
// exercise books at 35, handed over together and printed on one receipt.
//
// There is deliberately NO "owing" or "expected" column. None of these
// items is billed to anybody — nobody is charged for a uniform until
// they choose to walk in and buy one — so the amount is simply what was
// paid at the counter, typed in by the cashier.
function ItemLines({ types, items, setItems, label = 'Items paid for' }) {
  const setAt = (i, key, val) =>
    setItems(prev => prev.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));
  const addRow = () =>
    setItems(prev => [...prev, { income_type: '', amount: '', note: '' }]);
  const removeRow = (i) =>
    setItems(prev => (prev.length === 1 ? prev : prev.filter((_, idx) => idx !== i)));

  const total = items.reduce((s, r) => s + (Number(r.amount) || 0), 0);

  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.8 }}>
        <Typography variant="caption" sx={{
          textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800, fontSize: 10,
        }}>
          {label}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Chip size="small" color="success" variant="outlined"
          label={`GHS ${fmtMoney(total)}`} sx={{ fontWeight: 800, ...TNUM }} />
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, fontSize: 11 } }}>
              <TableCell sx={{ width: '38%' }}>Income type</TableCell>
              <TableCell align="right" sx={{ width: 140 }}>Amount</TableCell>
              <TableCell>Note (optional)</TableCell>
              <TableCell sx={{ width: 48 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((r, i) => (
              <TableRow key={i}>
                <TableCell>
                  <TextField select fullWidth size="small" value={r.income_type}
                    onChange={(e) => setAt(i, 'income_type', e.target.value)}>
                    <MenuItem value=""><em>— Select —</em></MenuItem>
                    {types.map(t => <MenuItem key={t.trans} value={t.type}>{t.type}</MenuItem>)}
                  </TextField>
                </TableCell>
                <TableCell align="right">
                  <TextField size="small" type="number" placeholder="0.00"
                    value={r.amount} onChange={(e) => setAt(i, 'amount', e.target.value)}
                    sx={{ width: 120, '& input': { textAlign: 'right', ...TNUM } }} />
                </TableCell>
                <TableCell>
                  <TextField size="small" fullWidth placeholder="e.g. 2 sets"
                    value={r.note} onChange={(e) => setAt(i, 'note', e.target.value)} />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title={items.length === 1 ? 'At least one line is needed' : 'Remove line'}>
                    <span>
                      <Button size="small" color="error" disabled={items.length === 1}
                        onClick={() => removeRow(i)}
                        sx={{ minWidth: 0, px: 1, fontWeight: 800 }}>×</Button>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Button size="small" onClick={addRow} startIcon={<AddIcon />}
        sx={{ mt: 0.8, textTransform: 'none', fontWeight: 700 }}>
        Add item
      </Button>
    </Box>
  );
}

// ── Student purchase — buying things that are not billed ──────
// The parent or learner walks in and buys: uniform, exercise books, a
// crest. The student is picked so the receipt carries the name, index
// number and class the school already holds — nothing is deducted from
// any account, because no account exists for these items.
function StudentPurchaseForm({ types, classes, bankAccts, onSaved }) {
  const [v, setV] = useState({
    term: '', acc_yer: defaultAcademicYear(),
    pay_date: new Date().toISOString().slice(0, 10),
    mode: 'Cash', bank_account: '', reference_no: '', notes: '',
    payer_name: '',
  });
  const set = (k, val) => setV(p => ({ ...p, [k]: val }));

  const [cla, setCla] = useState('');
  const [students, setStudents] = useState([]);
  const [loadingStu, setLoadingStu] = useState(false);
  const [student, setStudent] = useState(null);
  const [items, setItems] = useState([{ income_type: '', amount: '', note: '' }]);
  const [submitting, setSub] = useState(false);
  const [sendSMS, setSendSMS] = useState(false);
  const [sendWA, setSendWA] = useState(false);

  // Roster for the picker. Narrowing by class keeps the list short in a
  // big school; leaving it blank searches everyone.
  useEffect(() => {
    let on = true;
    setLoadingStu(true);
    API.get('/other-payments/students', { params: { cla, limit: 2000 } })
      .then(r => { if (on) setStudents(r.data || []); })
      .catch(() => { if (on) setStudents([]); })
      .finally(() => { if (on) setLoadingStu(false); });
    return () => { on = false; };
  }, [cla]);

  // A class change can orphan the chosen student.
  useEffect(() => {
    if (student && cla && student.class_name !== cla) setStudent(null);
  }, [cla, student]);

  const cleanItems = useMemo(
    () => items
      .map(r => ({
        income_type: String(r.income_type || '').trim(),
        amount: Number(r.amount) || 0,
        note: String(r.note || '').trim(),
      }))
      .filter(r => r.income_type && r.amount > 0),
    [items]
  );
  const total = cleanItems.reduce((s, r) => s + r.amount, 0);

  const canSave = !!student && cleanItems.length > 0 && v.pay_date && v.mode && v.bank_account;

  const submit = async () => {
    if (!canSave) return;
    const lines = cleanItems.map(r => `• ${r.income_type} — GHS ${fmtMoney(r.amount)}`).join('<br/>');
    const ok = await swal.confirm({
      title: 'Record this purchase?',
      html: `<b>${student.name}</b> · ${student.index_no} · ${student.class_name || '—'}<br/><br/>`
          + `${lines}<br/><br/><b>Total: GHS ${fmtMoney(total)}</b>`,
      confirmText: 'Save',
    });
    if (!ok) return;
    setSub(true);
    try {
      const r = await API.post('/other-payments/student-purchase', {
        student_id: student.index_no,
        items: cleanItems,
        payer_name: v.payer_name || student.name,
        pay_date: v.pay_date, mode: v.mode, bank_account: v.bank_account,
        reference_no: v.reference_no, notes: v.notes,
        send_sms: sendSMS, send_whatsapp: sendWA,
      });
      swal.success(r.data?.message || 'Saved');
      onSaved?.(r.data);
      setItems([{ income_type: '', amount: '', note: '' }]);
      setV(p => ({ ...p, reference_no: '', notes: '', payer_name: '' }));
      setStudent(null);
    } catch (e) { swal.error(swal.pickError(e, 'Save failed')); }
    finally { setSub(false); }
  };

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 2, mb: 2 }}>
      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <CartIcon color="primary" />
            <Typography fontWeight={800}>Student Purchase</Typography>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.5 }}>
            <TextField select size="small" label="Class (filter)" sx={{ minWidth: 180 }}
              value={cla} onChange={(e) => setCla(e.target.value)}>
              <MenuItem value=""><em>All classes</em></MenuItem>
              {classes.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
            <Autocomplete fullWidth size="small" options={students} loading={loadingStu}
              value={student} onChange={(_, val) => setStudent(val)}
              isOptionEqualToValue={(a, b) => a.index_no === b.index_no}
              getOptionLabel={(o) => `${o.name} · ${o.index_no}`}
              renderOption={(props, o) => (
                <li {...props} key={o.index_no}>
                  <Stack>
                    <Typography variant="body2" fontWeight={700}>{o.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {o.index_no}{o.class_name ? ` · ${o.class_name}` : ''}
                    </Typography>
                  </Stack>
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Student — search by name or index no" required
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>{loadingStu ? <CircularProgress size={16} /> : null}
                        {params.InputProps.endAdornment}</>
                    ),
                  }} />
              )} />
          </Stack>

          {student ? (
            <Alert severity="success" variant="outlined" sx={{ mb: 1.5 }}>
              <b>{student.name}</b> · {student.index_no}
              {student.class_name ? ` · ${student.class_name}` : ''}
              {student.type ? ` · ${student.type}` : ''}
            </Alert>
          ) : (
            <Alert severity="info" variant="outlined" sx={{ mb: 1.5 }}>
              Pick the student the receipt should be issued to. Nothing is deducted
              from any account — these items are not billed, so you type what was paid.
            </Alert>
          )}

          <ItemLines types={types} items={items} setItems={setItems} />

          <TextField fullWidth size="small" label="Paid by (if not the student)"
            value={v.payer_name} onChange={(e) => set('payer_name', e.target.value)} />
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <ReceiptIcon color="primary" />
            <Typography fontWeight={800}>Payment Entry</Typography>
          </Stack>
          <PaymentModePanel values={v} set={set}
            sendSMS={sendSMS} setSendSMS={setSendSMS}
            sendWA={sendWA} setSendWA={setSendWA} bankAccts={bankAccts} />

          <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, bgcolor: (t) => alpha(t.palette.success.main, 0.08) }}>
            <Typography variant="caption" sx={{
              textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800, fontSize: 10,
            }}>
              Total
            </Typography>
            <Typography variant="h5" fontWeight={800} color="success.main" sx={TNUM}>
              GHS {fmtMoney(total)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {cleanItems.length} item{cleanItems.length === 1 ? '' : 's'}
            </Typography>
          </Box>

          <Button fullWidth variant="contained" size="large" sx={{ mt: 1.5, fontWeight: 800 }}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <ReceiptIcon />}
            disabled={!canSave || submitting} onClick={submit}>
            {submitting ? 'Saving…' : 'Save Purchase'}
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
}

// ── Class Collection ──────────────────────────────────────────
function ClassCollectionForm({ types, classes, bankAccts, onSaved }) {
  const [v, setV] = useState({
    class_name: '',
    term: '', acc_yer: defaultAcademicYear(),
    pay_date: new Date().toISOString().slice(0, 10),
    mode: 'Cash', bank_account: '', reference_no: '', notes: '',
    payer_name: '',
  });
  // Several income types collected in one sitting — Loan 100, Excursion
  // 250 — instead of one save per type. The class total is their sum, so
  // the two can never disagree.
  const [items, setItems] = useState([{ income_type: '', amount: '', note: '' }]);
  const [allocMethod, setAllocMethod] = useState('NONE');  // NONE | EQUAL_SPLIT | MANUAL
  const [students, setStudents] = useState([]);            // for EQUAL_SPLIT
  const [studentsLoading, setSL] = useState(false);
  const [allocations, setAllocs] = useState([]);           // [{index_no, name, class_name, amount_paid, remarks}]
  const [submitting, setSub] = useState(false);
  const [sendSMS, setSendSMS] = useState(false);
  const [sendWA, setSendWA]   = useState(false);
  const set = (k, val) => setV(p => ({ ...p, [k]: val }));

  // Whenever class changes, reload its students for EQUAL_SPLIT/MANUAL
  useEffect(() => {
    if (!v.class_name) { setStudents([]); setAllocs([]); return; }
    setSL(true);
    API.get('/other-payments/students', { params: { cla: v.class_name, limit: 1000 } })
      .then(r => {
        const list = r.data || [];
        setStudents(list);
        // Seed manual allocations rows once class loads
        setAllocs(list.map(s => ({
          index_no: s.index_no, name: s.name, class_name: s.class_name,
          amount_paid: '', remarks: '',
        })));
      })
      .catch(() => { setStudents([]); setAllocs([]); })
      .finally(() => setSL(false));
  }, [v.class_name]);

  const cleanItems = useMemo(
    () => items
      .map(r => ({
        income_type: String(r.income_type || '').trim(),
        amount: Number(r.amount) || 0,
        note: String(r.note || '').trim(),
      }))
      .filter(r => r.income_type && r.amount > 0),
    [items]
  );
  const total = cleanItems.reduce((s, r) => s + r.amount, 0);
  const equalSplit = students.length > 0 ? Number((total / students.length).toFixed(2)) : 0;
  const manualSum = useMemo(
    () => allocations.reduce((s, a) => s + (Number(a.amount_paid) || 0), 0),
    [allocations]
  );
  const manualOk = Math.abs(manualSum - total) <= 0.01;

  const setManualAt = (i, key, val) =>
    setAllocs(p => p.map((a, idx) => idx === i ? { ...a, [key]: val } : a));

  const fillEqualToManual = () => {
    if (!students.length) return;
    setAllocs(students.map(s => ({
      index_no: s.index_no, name: s.name, class_name: s.class_name,
      amount_paid: equalSplit.toFixed(2), remarks: '',
    })));
  };

  const canSave = cleanItems.length > 0 && v.class_name && v.pay_date && v.mode && v.bank_account
    && total > 0 && !submitting
    && (allocMethod !== 'MANUAL' || manualOk)
    && (allocMethod !== 'EQUAL_SPLIT' || students.length > 0);

  const submit = async () => {
    if (!canSave) return;
    const ok = await swal.confirm({
      title: 'Record class collection?',
      tone: 'primary',
      html: `<div style="text-align:left;font-size:0.92rem">
        <div><b>${v.class_name}</b></div>
        <div style="margin-top:6px">${cleanItems
          .map(r => `• ${r.income_type} — GHS ${fmtMoney(r.amount)}`).join('<br/>')}</div>
        <div style="margin-top:6px;color:#475569">${v.mode} · ${v.bank_account}</div>
        <div style="margin-top:6px">Allocation: <b>${allocMethod}</b></div>
        <div style="margin-top:10px;font-weight:800">Total: GHS ${fmtMoney(total)}</div>
      </div>`,
      confirmText: 'Yes, save',
    });
    if (!ok) return;
    setSub(true);
    try {
      const payload = {
        ...v, items: cleanItems, total_amount: total,
        allocation_method: allocMethod,
        students: allocMethod === 'EQUAL_SPLIT' ? students : [],
        allocations: allocMethod === 'MANUAL'
          ? allocations.filter(a => Number(a.amount_paid) > 0).map(a => ({
              index_no: a.index_no, name: a.name, class_name: a.class_name,
              amount_paid: Number(a.amount_paid), remarks: a.remarks,
            }))
          : [],
      };
      const r = await API.post('/other-payments/class-collection', payload);
      swal.success(
        `Receipt ${r.data.receiptNo || r.data.receipt_no || ''} · GHS ${fmtMoney(r.data.total ?? total)}`,
        'Class collection saved',
      );
      onSaved?.(r.data);
      setItems([{ income_type: '', amount: '', note: '' }]);
      setV(p => ({ ...p, payer_name: '', notes: '', reference_no: '' }));
    } catch (e) { swal.error(swal.pickError(e, 'Save failed')); }
    finally { setSub(false); }
  };

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 2 }}>
      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <CollectionIcon color="primary" />
            <Typography fontWeight={800}>Class Collection</Typography>
          </Stack>

          <Stack spacing={1.5} sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1}>
              <TextField select fullWidth size="small" label="Class"
                value={v.class_name} onChange={(e) => set('class_name', e.target.value)} required>
                <MenuItem value=""><em>— Select —</em></MenuItem>
                {classes.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
              <TextField fullWidth size="small" label="Representative / Payer"
                value={v.payer_name} onChange={(e) => set('payer_name', e.target.value)} />
            </Stack>
          </Stack>

          {/* Several income types in one sitting. The class total is the
              sum of these lines — there is no separate total to type, so
              it can never disagree with the lines. */}
          <ItemLines types={types} items={items} setItems={setItems}
            label="Income types collected" />

          <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} alignItems="center">
            <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800, fontSize: 10 }}>
              Student Allocation
            </Typography>
            <ToggleButtonGroup size="small" exclusive value={allocMethod}
              onChange={(_, v) => v && setAllocMethod(v)}>
              <ToggleButton value="NONE" sx={{ textTransform: 'none', fontWeight: 700 }}>None</ToggleButton>
              <ToggleButton value="EQUAL_SPLIT" disabled={!v.class_name}
                sx={{ textTransform: 'none', fontWeight: 700 }}>Equal split</ToggleButton>
              <ToggleButton value="MANUAL" disabled={!v.class_name}
                sx={{ textTransform: 'none', fontWeight: 700 }}>Manual</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          {allocMethod === 'NONE' && (
            <Alert severity="info" variant="outlined" sx={{ mb: 1 }}>
              Single class-level receipt — no per-student record will be created.
            </Alert>
          )}

          {allocMethod === 'EQUAL_SPLIT' && (
            <>
              {studentsLoading ? (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
                  <CircularProgress size={18} /><Typography variant="caption" color="text.secondary">Loading…</Typography>
                </Stack>
              ) : (
                <Alert severity="success" variant="outlined" sx={{ mb: 1 }}>
                  GHS {fmtMoney(total)} ÷ {students.length} student(s) ≈ <b>GHS {fmtMoney(equalSplit)}</b> each.
                </Alert>
              )}
            </>
          )}

          {allocMethod === 'MANUAL' && (
            <>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <Button size="small" variant="outlined" onClick={fillEqualToManual}
                  disabled={!students.length}
                  sx={{ textTransform: 'none', fontWeight: 700 }}>
                  Fill equal as starting point
                </Button>
                <Box sx={{ flex: 1 }} />
                <Chip size="small" color={manualOk ? 'success' : 'warning'}
                  label={`Allocated: GHS ${fmtMoney(manualSum)} / ${fmtMoney(total)}`}
                  sx={{ fontWeight: 700, ...TNUM }} />
              </Stack>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 380 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 700, fontSize: 11 } }}>
                      <TableCell>Index</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Remarks</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {studentsLoading && <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3 }}><CircularProgress size={18} /></TableCell></TableRow>}
                    {!studentsLoading && allocations.length === 0 && <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>No students.</TableCell></TableRow>}
                    {!studentsLoading && allocations.map((a, i) => (
                      <TableRow key={a.index_no}>
                        <TableCell sx={TNUM}>{a.index_no}</TableCell>
                        <TableCell>{a.name}</TableCell>
                        <TableCell align="right">
                          <TextField size="small" type="number"
                            value={a.amount_paid} onChange={(e) => setManualAt(i, 'amount_paid', e.target.value)}
                            sx={{ width: 110, '& input': { textAlign: 'right', ...TNUM, fontSize: 12 } }}
                            inputProps={{ style: { padding: '5px 8px' } }} />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" placeholder="—"
                            value={a.remarks} onChange={(e) => setManualAt(i, 'remarks', e.target.value)} fullWidth
                            inputProps={{ style: { padding: '5px 8px', fontSize: 12 } }} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </CardContent>
      </Card>

      <Card sx={{ alignSelf: 'flex-start', position: { md: 'sticky' }, top: { md: 100 } }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <ReceiptIcon color="primary" />
            <Typography fontWeight={800}>Payment Entry</Typography>
          </Stack>
          <Stack spacing={1} sx={{ mb: 1.5 }}>
            <Stack direction="row" spacing={1}>
              <TextField select size="small" label="Term" value={v.term} onChange={(e) => set('term', e.target.value)} sx={{ flex: 1 }}>
                <MenuItem value=""><em>—</em></MenuItem>
                {TERMS.map(t => <MenuItem key={t} value={t}>{t.replace('_', ' ')}</MenuItem>)}
              </TextField>
              <TextField size="small" label="Academic Year" value={v.acc_yer} onChange={(e) => set('acc_yer', e.target.value)} sx={{ flex: 1 }} />
            </Stack>
          </Stack>
          <PaymentModePanel values={v} set={set}
            sendSMS={sendSMS} setSendSMS={setSendSMS}
            sendWA={sendWA} setSendWA={setSendWA}
            bankAccts={bankAccts} />
          <Box sx={{ mt: 2, p: 1.2, borderRadius: 1.5, bgcolor: (t) => alpha(t.palette.success.main, 0.08) }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800, fontSize: 9 }}>Class Total</Typography>
                <Typography variant="caption" sx={{ display: 'block', fontSize: 10 }} color="text.secondary">
                  {allocMethod}
                </Typography>
              </Box>
              <Typography fontWeight={900} color="success.dark" sx={{ ...TNUM, fontSize: 18 }}>
                GHS {fmtMoney(total)}
              </Typography>
            </Stack>
          </Box>
          <Button size="large" fullWidth variant="contained" onClick={submit} disabled={!canSave}
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <ReceiptIcon />}
            sx={{ mt: 1.5, fontWeight: 800 }}>
            {submitting ? 'Saving…' : 'Save Class Collection'}
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
}

// ── Collection Sheet panel ─────────────────────────────────────
// Generates a printable empty grid teachers fill in by hand.
// Income types become column groups; days (Mon..Sat) become sub-columns.
const ALL_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const TERMS_CS = ['Term_1', 'Term_2', 'Term_3'];

function CollectionSheetPanel({ types, classes }) {
  const [cla, setCla] = useState('');
  const [selectedTypes, setSelectedTypes] = useState([]);   // type objects
  const [days, setDays] = useState(['Mon', 'Tue', 'Wed', 'Thu', 'Fri']);
  const [term, setTerm] = useState('');
  const [year, setYear] = useState(defaultAcademicYear());
  const [weekStart, setWeekStart] = useState(() => new Date().toISOString().slice(0, 10));

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [students, setStudents] = useState([]);
  const [school, setSchool] = useState(null);

  const toggleDay = (d) =>
    setDays(p => p.includes(d) ? p.filter(x => x !== d) : [...p, d]);

  const canGenerate = cla && selectedTypes.length > 0 && days.length > 0;

  const generate = async () => {
    if (!canGenerate) return;
    setOpen(true); setLoading(true); setError('');
    try {
      const [stuRes, infoRes] = await Promise.all([
        API.get('/other-payments/students', { params: { cla, limit: 1000 } }),
        API.get('/setup/info'),
      ]);
      setStudents(stuRes.data || []);
      // /setup/info returns { success, info } — pull the actual school row out.
      setSchool(infoRes.data?.info || infoRes.data || {});
    } catch (e) {
      setError(swal.pickError(e, 'Could not build collection sheet'));
    } finally { setLoading(false); }
  };

  return (
    <Card sx={{ mt: 2 }}>
      <CardContent sx={{ pb: '16px !important' }}>
        {/* Compact header — title + Generate on the same row */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap" rowGap={1}>
          <SheetIcon color="primary" />
          <Typography fontWeight={800}>Collection Sheet</Typography>
          <Chip size="small" variant="outlined" label="Printable handwriting sheet"
            sx={{ fontWeight: 700, height: 20, fontSize: 10 }} />
          <Box sx={{ flex: 1 }} />
          <Button variant="contained" startIcon={<PrintIcon fontSize="small" />}
            onClick={generate} disabled={!canGenerate}
            sx={{ fontWeight: 800, textTransform: 'none' }}>
            Generate Sheet
          </Button>
        </Stack>

        {/* Row 1 — class / term / year / week of  */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: '1.2fr 1fr 1fr 1.2fr' },
          gap: 1, mb: 1,
        }}>
          <TextField select size="small" label="Class" value={cla}
            onChange={(e) => setCla(e.target.value)} required>
            <MenuItem value=""><em>— Select —</em></MenuItem>
            {classes.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Term" value={term}
            onChange={(e) => setTerm(e.target.value)}>
            <MenuItem value=""><em>—</em></MenuItem>
            {TERMS_CS.map(t => <MenuItem key={t} value={t}>{t.replace('_', ' ')}</MenuItem>)}
          </TextField>
          <TextField size="small" label="Academic Year" value={year}
            onChange={(e) => setYear(e.target.value)} />
          <TextField size="small" type="date" label="Week of"
            InputLabelProps={{ shrink: true }} value={weekStart}
            onChange={(e) => setWeekStart(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><DateIcon fontSize="small" /></InputAdornment> }} />
        </Box>

        {/* Row 2 — income types (multi-select) + days (inline chips) */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
          gap: 1, alignItems: 'center',
        }}>
          <Autocomplete
            multiple size="small"
            options={types}
            getOptionLabel={(o) => o.type}
            isOptionEqualToValue={(a, b) => a.trans === b.trans}
            value={selectedTypes}
            onChange={(_, v) => setSelectedTypes(v)}
            limitTags={6}
            renderTags={(value, getTagProps) =>
              value.map((opt, i) => (
                <Chip key={opt.trans} size="small" color="primary"
                  label={opt.type} {...getTagProps({ index: i })}
                  sx={{ fontWeight: 700, height: 22 }} />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label="Income Types"
                placeholder={selectedTypes.length === 0 ? 'Pick one or more…' : ''} />
            )}
          />
          <Stack direction="row" spacing={0.5} alignItems="center" justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
            <Typography variant="caption" sx={{
              textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 800, fontSize: 10,
              color: 'text.secondary', mr: 0.5,
            }}>
              Days
            </Typography>
            {ALL_DAYS.map(d => (
              <Chip key={d} size="small" label={d}
                color={days.includes(d) ? 'primary' : 'default'}
                variant={days.includes(d) ? 'filled' : 'outlined'}
                onClick={() => toggleDay(d)}
                sx={{ fontWeight: 700, cursor: 'pointer', height: 24,
                  '& .MuiChip-label': { px: 0.9 } }} />
            ))}
          </Stack>
        </Box>
      </CardContent>

      <CollectionSheetDialog
        open={open} onClose={() => setOpen(false)}
        loading={loading} error={error}
        school={school} students={students}
        selectedTypes={selectedTypes} days={days}
        cla={cla} term={term} year={year} weekStart={weekStart}
      />
    </Card>
  );
}

// ── Top-level tab ──────────────────────────────────────────────
export default function OtherPaymentTab() {
  const [mode, setMode] = useState('STUDENT');   // STUDENT | CLASS
  const [types, setTypes] = useState([]);
  const [classes, setClasses] = useState([]);
  const [bankAccts, setBankAccts] = useState([]);

  // Receipt dialog
  const [rcptOpen, setRcptOpen] = useState(false);
  const [rcptData, setRcptData] = useState(null);
  const [rcptLoading, setRcptLd] = useState(false);
  const [rcptError, setRcptErr] = useState('');

  useEffect(() => {
    API.get('/other-payments/types').then(r => setTypes(r.data || [])).catch(() => {});
    API.get('/classes').then(r => setClasses((r.data || []).map(c => c.class).filter(Boolean))).catch(() => {});
    API.get('/payments/bank-accounts').then(r => setBankAccts(r.data || [])).catch(() => {});
  }, []);

  const openReceipt = async (txId) => {
    setRcptOpen(true); setRcptLd(true); setRcptData(null); setRcptErr('');
    try {
      const r = await API.get(`/other-payments/${txId}/receipt`);
      setRcptData(r.data);
    } catch (e) { setRcptErr(swal.pickError(e, 'Could not load receipt')); }
    finally { setRcptLd(false); }
  };

  const handleSaved = (r) => {
    if (r?.transaction_id) openReceipt(r.transaction_id);
  };

  return (
    <Box sx={{ fontFamily: FONT_ACCT }}>
      {/* Two ways money arrives here, and they are genuinely different
          jobs: one learner buying things at the counter, versus a whole
          class handing in a collection. Neither involves a balance. */}
      <ToggleButtonGroup size="small" exclusive value={mode}
        onChange={(_, m) => m && setMode(m)} sx={{ mb: 2 }}>
        <ToggleButton value="STUDENT" sx={{ textTransform: 'none', fontWeight: 800, px: 2 }}>
          <CartIcon fontSize="small" sx={{ mr: 0.8 }} /> Student purchase
        </ToggleButton>
        <ToggleButton value="CLASS" sx={{ textTransform: 'none', fontWeight: 800, px: 2 }}>
          <CollectionIcon fontSize="small" sx={{ mr: 0.8 }} /> Class collection
        </ToggleButton>
      </ToggleButtonGroup>

      {mode === 'STUDENT' ? (
        <StudentPurchaseForm
          types={types} classes={classes} bankAccts={bankAccts}
          onSaved={handleSaved}
        />
      ) : (
        <ClassCollectionForm
          types={types} classes={classes} bankAccts={bankAccts}
          onSaved={handleSaved}
        />
      )}
      <CollectionSheetPanel types={types} classes={classes} />
      <OtherReceiptDialog
        open={rcptOpen} onClose={() => setRcptOpen(false)}
        data={rcptData} loading={rcptLoading} error={rcptError}
      />
    </Box>
  );
}
